import { Alert, Button, Card, Form, Input, message, Typography, Upload } from "antd"
import { observer } from "mobx-react"
import { useContainerInjection } from "../../hooks/useContainerInjection"
import { UsersViewModel } from "./UsersViewModel"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { useEffect, useMemo, useState } from "react"
import { RoutePaths } from "../../navigation/RoutePaths.enum"
import { User } from "../../../domain/entities/User"
import { useTranslation } from "react-i18next"
import ReactQuill from "react-quill"
import "react-quill/dist/quill.snow.css"
import { UploadOutlined } from "@ant-design/icons"

const { Item } = Form
const { Text } = Typography

const SUBJECT_MAX_LENGTH = 180
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024

const isEmailValid = (value: string) => /.+@.+\..+/.test(value)
const stripHtml = (value: string) =>
	value
		.replace(/<[^>]*>/g, "")
		.replace(/&nbsp;/g, " ")
		.trim()

const quillModules = {
	toolbar: [["bold", "italic", "underline"], [{ list: "ordered" }, { list: "bullet" }], ["link"], ["clean"]]
}

const UsersNotifyView = () => {
	const { t } = useTranslation("users", { keyPrefix: "notify" })
	const navigate = useNavigate()
	const location = useLocation()
	const viewModel = useContainerInjection<UsersViewModel>("UsersViewModel")
	const { id } = useParams<{ id: string }>()
	const locationState = location.state as { user?: User } | undefined

	const [userEmail, setUserEmail] = useState("")
	const [subject, setSubject] = useState("")
	const [messageHtml, setMessageHtml] = useState("")
	const [attachmentFile, setAttachmentFile] = useState<File | null>(null)
	const [attachmentError, setAttachmentError] = useState("")
	const [sending, setSending] = useState(false)
	const [notificationSent, setNotificationSent] = useState(false)

	useEffect(() => {
		if (locationState?.user?.email && isEmailValid(locationState.user.email)) {
			setUserEmail(locationState.user.email)
			return
		}

		if (!id) {
			message.error(t("userNotFound"))
			navigate(RoutePaths.USERS, { replace: true })
			return
		}

		viewModel
			.fetchUserDetail(id)
			.then(user => {
				if (!user?.email || !isEmailValid(user.email)) {
					message.error(t("userNotFound"))
					navigate(RoutePaths.USERS, { replace: true })
					return
				}
				setUserEmail(user.email)
			})
			.catch(() => {
				message.error(t("userNotFound"))
				navigate(RoutePaths.USERS, { replace: true })
			})
	}, [id, locationState?.user?.email, navigate, t, viewModel])

	const subjectCounter = useMemo(() => `${subject.length}/${SUBJECT_MAX_LENGTH}`, [subject.length])

	const onSubmit = async () => {
		if (!userEmail || !isEmailValid(userEmail)) {
			message.error(t("userNotFound"))
			navigate(RoutePaths.USERS, { replace: true })
			return
		}

		if (!subject.trim()) {
			message.error(t("subjectRequired"))
			return
		}

		if (subject.length > SUBJECT_MAX_LENGTH) {
			message.error(t("subjectMax"))
			return
		}

		if (attachmentError) {
			message.error(attachmentError)
			return
		}

		if (!stripHtml(messageHtml)) {
			message.error(t("messageRequired"))
			return
		}

		setSending(true)
		try {
			await viewModel.sendUserNotification({
				userEmail,
				subject: subject.trim(),
				messageHtml,
				attachmentFile: attachmentFile || undefined
			})
			setSubject("")
			setMessageHtml("")
			setAttachmentFile(null)
			setAttachmentError("")
			setNotificationSent(true)
		} catch (error: any) {
			if (error?.httpStatus === 404) {
				message.error(t("sendError"))
				return
			}
			message.error(t("sendError"))
		} finally {
			setSending(false)
		}
	}

	return (
		<Card
			bordered={false}
			className="criclebox tablespace mb-24"
			title={<h6 className="font-semibold m-0">{t("title")}</h6>}
			bodyStyle={{ padding: "24px" }}
		>
			{notificationSent && (
				<Alert type="success" message={t("sentSuccess")} showIcon style={{ marginBottom: 16 }} />
			)}

			<Form layout="vertical">
				<Item label={t("email")}>
					<Input value={userEmail} disabled />
				</Item>
				<Item
					label={t("attachment")}
					validateStatus={attachmentError ? "error" : undefined}
					help={
						attachmentError ? (
							<Text type="danger">{attachmentError}</Text>
						) : (
							<Text type="secondary">{t("attachmentOptional")}</Text>
						)
					}
				>
					<Upload
						maxCount={1}
						showUploadList={attachmentFile !== null}
						fileList={attachmentFile ? [{ uid: "-2", name: attachmentFile.name, status: "done" }] : []}
						onRemove={() => {
							setAttachmentFile(null)
							setAttachmentError("")
						}}
						beforeUpload={async file => {
							if (file.size > MAX_FILE_SIZE_BYTES) {
								setAttachmentError(t("attachmentMaxSize"))
								setAttachmentFile(null)
								return false
							}

							const fileBuffer = await file.arrayBuffer()
							const fileSnapshot = new File([fileBuffer], file.name, {
								type: file.type || "application/octet-stream"
							})
							setAttachmentError("")
							setAttachmentFile(fileSnapshot)
							return false
						}}
					>
						<Button icon={<UploadOutlined />}>{t("selectAttachment")}</Button>
					</Upload>
				</Item>
				<Item label={t("subject")} required>
					<Input
						value={subject}
						onChange={e => setSubject(e.target.value)}
						maxLength={SUBJECT_MAX_LENGTH}
						placeholder={t("subjectPlaceholder")}
						suffix={subjectCounter}
					/>
				</Item>
				<Item label={t("message")} required>
					<ReactQuill
						className="users-notify-editor"
						value={messageHtml}
						onChange={setMessageHtml}
						modules={quillModules}
					/>
				</Item>

				<div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 24 }}>
					<Button onClick={() => navigate(RoutePaths.USERS)}>{t("back")}</Button>
					<Button type="primary" loading={sending} disabled={sending} onClick={onSubmit}>
						{t("send")}
					</Button>
				</div>
			</Form>
		</Card>
	)
}

export default observer(UsersNotifyView)
